<template>
  <div class="slider-range-root">
    <div v-if="show_inputs && summaryText" class="slider-range-header">
      <span v-if="label" class="slider-range-title">{{ label }}</span>
      <span class="slider-range-summary">{{ summaryText }}</span>
    </div>

    <div v-if="show_inputs" class="slider-range-inputs">
      <div class="slider-range-field">
        <label class="slider-range-label m-0">{{ $t('search.from') }}</label>
        <cleave
          v-model="from"
          class="form-control form-control-sm text-center"
          :raw="false"
          :options="options.number"
          :placeholder="$t('search.min')"
          @input="changeInput"
          @blur="endChangeInput"
        />
      </div>
      <div class="slider-range-field">
        <label class="slider-range-label m-0">{{ $t('search.to') }}</label>
        <cleave
          v-model="to"
          class="form-control form-control-sm text-center"
          :raw="false"
          :options="options.number"
          :placeholder="$t('search.max')"
          @input="changeInput"
          @blur="endChangeInput"
        />
      </div>
    </div>

    <vue-slider
      class="slider-range-slider"
      :tooltip="this.tooltip"
      v-model="sliderModel"
      :direction="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
      :max="max_value"
      :min="min_value"
      :interval="interval"
      @drag-end="changeSlide"
      :tooltip-formatter="formatter_commas"
    >
      <template v-slot:dot="{ value }">
        <div :class="getClasses(value)"></div>
      </template>

      <template v-slot:process="{ style, index }">
        <div class="vue-slider-process custom-class" :style="[style, getProgressStyle(style, index)]">
        </div>
      </template>
    </vue-slider>

    <div v-if="show_inputs" class="d-flex justify-content-between slider-range-bounds">
      <span>{{ separatebycomma(min_value) }}</span>
      <span>{{ separatebycomma(max_value) }}{{ max_plus ? '+' : '' }}</span>
    </div>

    <div v-if="show_inputs" class="slider-range-chips" @click.stop>
      <button
        v-for="(preset, index) in presetList"
        :key="index"
        type="button"
        class="slider-range-chip"
        :class="{ 'slider-range-chip--active': isPresetActive(preset) }"
        @mousedown.prevent
        @click.stop="applyPreset(preset)"
      >{{ preset.label }}</button>
      <button type="button" class="slider-range-chip" @mousedown.prevent @click.stop="clearRange">{{ $t('search.clear') }}</button>
    </div>

    <small v-if="show_inputs" class="d-block text-muted slider-range-hint">{{ $t('search.rangeHint') }}</small>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component'
import Cleave from 'vue-cleave-component'
import { separatebycomma, trimNumber } from '@/libs/utils/formats'

export default {
  name: 'SliderRange',
  props: {
    min_value: {
      type: Number,
      required: true
    },
    max_value: {
      type: Number,
      required: true
    },
    interval: {
      type: Number,
      required: true
    },
    init_range: {
      type: Array,
      required: true
    },
    identifier: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: 'always'
    },
    show_inputs: {
      type: Boolean,
      default: true
    },
    // Texto a la izquierda del resumen (opcional, p.ej. "Edad").
    label: {
      type: String,
      default: ''
    },
    // Sufijo del resumen (p.ej. "años").
    unit: {
      type: String,
      default: ''
    },
    // Texto cuando el rango está completamente abierto (p.ej. "Cualquier edad").
    any_text: {
      type: String,
      default: ''
    },
    // Añade "+" a la etiqueta del extremo máximo.
    max_plus: {
      type: Boolean,
      default: true
    },
    // Chips de acceso rápido: array [{ label, from, to }] o la palabra clave 'age'.
    presets: {
      type: [Array, String],
      default: () => []
    },
    // Permite que el input "Hasta" supere el max_value (el slider se queda en el tope).
    no_max_limit: {
      type: Boolean,
      default: false
    }
  },
  components: {
    VueSlider,
    Cleave
  },
  data () {
    return {
      separatebycomma,
      formatter_commas: v => `${separatebycomma(v)}`,
      range: this.init_range,
      from: null,
      to: null,
      options: {
        number: {
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralPositiveOnly: true
        }
      }
    }
  },
  created () {
    this.formatRange()
  },
  computed: {
    // El slider siempre se mueve dentro de [min, max]; el valor real (range) puede
    // exceder max cuando no_max_limit esta activo (via input "Hasta").
    sliderModel: {
      get () {
        return [
          Math.min(Math.max(this.range[0], this.min_value), this.max_value),
          Math.min(Math.max(this.range[1], this.min_value), this.max_value)
        ]
      },
      set (val) { this.range = val }
    },
    presetList () {
      if (Array.isArray(this.presets)) return this.presets
      if (this.presets === 'age') {
        return [
          { label: '18-24', from: 18, to: 24 },
          { label: '25-34', from: 25, to: 34 },
          { label: '35+', from: 35, to: this.max_value }
        ]
      }
      return []
    },
    summaryText () {
      const lo = this.range[0]
      const hi = this.range[1]
      const suffix = this.unit ? ` ${this.unit}` : ''
      const openLow = lo <= this.min_value
      const openHigh = this.no_max_limit ? hi === this.max_value : hi >= this.max_value
      if (openLow && openHigh) return this.any_text
      if (openHigh) return `${this.$t('search.from')} ${separatebycomma(lo)}${suffix}`
      if (openLow) return `${this.$t('search.to')} ${separatebycomma(hi)}${suffix}`
      return `${this.$t('search.between')} ${separatebycomma(lo)} ${this.$t('search.and')} ${separatebycomma(hi)}${suffix}`
    }
  },
  methods: {
    // Limita un valor a [min, max] y lo alinea al intervalo.
    clampVal (v) {
      let n = Math.max(v, this.min_value)
      if (!this.no_max_limit) n = Math.min(n, this.max_value)
      n = Math.round(n / this.interval) * this.interval
      return n
    },
    // Convierte el valor de un input a número válido. Vacío => extremo (rango abierto).
    toNumber (val, fallback) {
      if (val === null || val === undefined || val === '') return fallback
      const n = typeof val === 'number' ? val : trimNumber(val)
      if (isNaN(n)) return fallback
      return this.clampVal(n)
    },
    // Sincroniza los inputs a partir del rango. Extremo => vacío (placeholder).
    formatRange () {
      this.from = this.range[0] <= this.min_value ? null : this.range[0]
      // Con no_max_limit, solo el tope exacto (max) cuenta como "abierto"; valores
      // mayores se muestran tal cual.
      if (this.no_max_limit) {
        this.to = this.range[1] === this.max_value ? null : this.range[1]
      } else {
        this.to = this.range[1] >= this.max_value ? null : this.range[1]
      }
    },
    // Mientras se escribe: mueve el slider en vivo, sin emitir todavía.
    changeInput () {
      const from = this.toNumber(this.from, this.min_value)
      const to = this.toNumber(this.to, this.max_value)
      this.range = from <= to ? [from, to] : [to, from]
    },
    // Al salir del input: normaliza, refresca campos y emite.
    endChangeInput () {
      this.changeInput()
      this.formatRange()
      this.emitChange()
    },
    changeSlide () {
      this.formatRange()
      this.emitChange()
    },
    applyPreset (preset) {
      this.range = [this.clampVal(preset.from), this.clampVal(preset.to)]
      this.formatRange()
      this.emitChange()
    },
    clearRange () {
      this.range = [this.min_value, this.max_value]
      this.formatRange()
      this.emitChange()
    },
    isPresetActive (preset) {
      return this.range[0] === this.clampVal(preset.from) && this.range[1] === this.clampVal(preset.to)
    },
    emitChange () {
      this.$emit('changeSlide', this.identifier, this.range)
    },
    getClasses (value) {
      if (value === this.min_value || value === this.max_value) return 'value-inactive'
      return 'value-active'
    },
    getProgressStyle (style) {
      if (this.range[0] !== this.min_value && this.range[1] !== this.max_value) {
        return {
          'left': style.left,
          'width': style.width,
          'background-color': '#7367f0'
        }
      } else if (this.range[0] === this.min_value && this.range[1] !== this.max_value) {
        const width = 100 - parseFloat(style.width)
        return {
          'left': style.width,
          'width': `${width}%`,
          'background-color': '#7367f0'
        }
      }

      const left = this.range[1] === this.max_value ? 0 : style.width;
      const width = this.range[0] === this.min_value ? style.width : style.left;
      return {
        left,
        width,
        'background-color': '#7367f0'
      }
    },
  },
}
</script>

<style scoped>
.slider-range-root {
  white-space: normal;
  max-width: 100%;
}
.slider-range-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  min-width: 0;
}
.slider-range-title {
  font-size: 13px;
  font-weight: 600;
  color: #5e5873;
  margin-right: auto;
  white-space: nowrap;
}
.slider-range-summary {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #7367f0;
  text-align: right;
  white-space: normal;
  word-break: break-word;
}
.slider-range-inputs {
  display: flex;
  gap: 10px;
}
.slider-range-field {
  flex: 1 1 0;
  min-width: 0;
}
.slider-range-label {
  font-size: 11px;
  color: #b9b9c3;
  text-align: left;
}
.slider-range-inputs .form-control {
  width: 100%;
  height: 34px;
}
.slider-range-slider {
  margin-top: 2px;
  margin-bottom: 0;
}
.slider-range-slider ::v-deep .vue-slider {
  margin: 0;
}
.slider-range-bounds {
  font-size: 11px;
  color: #b9b9c3;
  margin-top: 2px;
}
.slider-range-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.slider-range-chip {
  border: 1px solid #d8d6de;
  background: transparent;
  color: #6e6b7b;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.15s ease;
}
.slider-range-chip:hover {
  border-color: #7367f0;
  color: #7367f0;
}
.slider-range-chip--active,
.slider-range-chip--active:hover {
  background: #7367f0;
  border-color: #7367f0;
  color: #fff;
}
.slider-range-hint {
  display: block;
  font-size: 11px;
  margin-top: 10px;
  white-space: normal;
}
</style>
